/**
 * VerifyEmailPage - Handles email verification from link click
 */
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2, Mail, RefreshCw } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [status, setStatus] = useState("verifying"); // verifying, success, error, already_verified
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    if (token) {
      verifyEmail(token);
    } else {
      setStatus("error");
      setMessage("Invalid verification link. No token provided.");
    }
  }, [token]);

  const verifyEmail = async (verificationToken) => {
    try {
      const response = await axios.get(`${API}/email-verification/verify/${verificationToken}`);
      
      if (response.data.success) {
        if (response.data.already_verified) {
          setStatus("already_verified");
          setMessage(response.data.message);
        } else {
          setStatus("success");
          setMessage(response.data.message);
          setEmail(response.data.email);
        }
      }
    } catch (error) {
      setStatus("error");
      setMessage(error.response?.data?.detail || "Verification failed. The link may be expired or invalid.");
    }
  };

  const handleGoToLogin = () => {
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl border-0">
        <CardHeader className="text-center pb-2">
          {status === "verifying" && (
            <>
              <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
              <CardTitle className="text-2xl">Verifying Email</CardTitle>
              <CardDescription>Please wait while we verify your email address...</CardDescription>
            </>
          )}
          
          {status === "success" && (
            <>
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <CardTitle className="text-2xl text-green-700">Email Verified!</CardTitle>
              <CardDescription className="text-green-600">{message}</CardDescription>
            </>
          )}
          
          {status === "already_verified" && (
            <>
              <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-blue-600" />
              </div>
              <CardTitle className="text-2xl text-blue-700">Already Verified</CardTitle>
              <CardDescription className="text-blue-600">{message}</CardDescription>
            </>
          )}
          
          {status === "error" && (
            <>
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <CardTitle className="text-2xl text-red-700">Verification Failed</CardTitle>
              <CardDescription className="text-red-600">{message}</CardDescription>
            </>
          )}
        </CardHeader>
        
        <CardContent className="space-y-4 pt-4">
          {(status === "success" || status === "already_verified") && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <p className="text-green-800">
                  Your 30-day free trial is now active! Explore all features and see how Today's Kidz can help manage your childcare center.
                </p>
              </div>
              <Button 
                onClick={handleGoToLogin} 
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                size="lg"
              >
                Continue to Login
              </Button>
            </div>
          )}
          
          {status === "error" && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-amber-800 text-sm">
                  <strong>Need a new verification link?</strong><br />
                  Go to the login page and click "Resend Verification Email".
                </p>
              </div>
              <Button 
                onClick={handleGoToLogin} 
                variant="outline"
                className="w-full"
                size="lg"
              >
                Go to Login
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmailPage;
